"use client";

import { useEffect, useRef, useState } from "react";

import { useUser } from "@auth0/nextjs-auth0/client";

const HARD_LOGOUT_TIME = 60 * 60 * 1000; // 1 hour - hard limit
const INACTIVITY_LOGOUT = 30 * 60 * 1000; // 30 minutes - inactivity limit
const WARNING_BEFORE = 5 * 60 * 1000; // 5 minutes warning

export const useSessionManager = () => {
  const { user } = useUser();
  const [showWarning, setShowWarning] = useState(false);
  const [warningType, setWarningType] = useState<"inactivity" | "hardLimit">("inactivity");
  const [remainingTime, setRemainingTime] = useState(INACTIVITY_LOGOUT);
  const lastActivityRef = useRef(Date.now());
  const sessionStartRef = useRef(Date.now());

  const logout = () => {
    window.location.assign(`/api/auth/logout?returnTo=${encodeURIComponent(window.location.origin)}`);
  };

  const resetInactivityTimer = () => {
    const timeSinceStart = Date.now() - sessionStartRef.current;
    // Only reset if we haven't hit the hard limit
    if (timeSinceStart < HARD_LOGOUT_TIME) {
      lastActivityRef.current = Date.now();
      setShowWarning(false);
    }
  };

  useEffect(() => {
    if (!user) return;

    // Reset session start time
    sessionStartRef.current = Date.now();

    const checkSession = () => {
      const now = Date.now();
      const timeSinceStart = now - sessionStartRef.current;
      const timeSinceActivity = now - lastActivityRef.current;

      // Check hard limit first
      if (timeSinceStart >= HARD_LOGOUT_TIME) {
        logout();
        return;
      }

      // Show hard limit warning
      if (timeSinceStart >= (HARD_LOGOUT_TIME - WARNING_BEFORE) && !showWarning) {
        setWarningType("hardLimit");
        setShowWarning(true);
        setRemainingTime(HARD_LOGOUT_TIME - timeSinceStart);
        return;
      }

      // Check inactivity
      if (timeSinceActivity >= INACTIVITY_LOGOUT) {
        logout();
        return;
      }

      // Show inactivity warning
      if (timeSinceActivity >= (INACTIVITY_LOGOUT - WARNING_BEFORE) && !showWarning) {
        setWarningType("inactivity");
        setShowWarning(true);
        setRemainingTime(INACTIVITY_LOGOUT - timeSinceActivity);
      }
    };

    const intervalId = setInterval(checkSession, 10000); // Check every 10 seconds

    // Add user activity listeners
    const events = ["mousemove", "keypress", "click", "scroll", "touchstart"];
    events.forEach(event => {
      window.addEventListener(event, resetInactivityTimer);
    });

    return () => {
      clearInterval(intervalId);
      events.forEach(event => {
        window.removeEventListener(event, resetInactivityTimer);
      });
    };
  }, [user]);

  return {
    showWarning,
    warningType,
    remainingTime,
    extendSession: resetInactivityTimer,
    dismissWarning: () => setShowWarning(false)
  };
};
